import Button from "@mui/material/Button";
import Dialog from "components/NoScrollDialog";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import DialogTitle from "@mui/material/DialogTitle";
import Grid from "@mui/material/Grid";
import TextField from "@mui/material/TextField";
import DropdownSelect from "components/DropdownSelect";
import Avatar from "components/FileAvatar";
import LoadingButton from "components/LoadingButton";
import MDBox from "components/MDBox";
import MDSelect from "components/MDSelect";
import { useLanguage } from "contexts/language.context";
import * as React from "react";
import toast from "react-hot-toast";
import userGroupsService from "services/user-groups-service";

export default function BranchGroupDialog({
  isOpen,
  action,
  data,
  onSubmit,
  fetchSubscriptions,
  fetchColorPalettes,
  fetchAccountManagers,
  fetchAccounts,
}) {
  const { label } = useLanguage();
  const [open, setOpen] = React.useState(false);
  const [loading, setLoading] = React.useState(false);
  const [value, setValue] = React.useState({
    name: "",
    email: "",
    phone: "",
    logo: "",
    colorPaletteId: 1,
    //subscriptionId: 1,
    accountManagerId: 1,
    accountId: data.accountId,
  });
  const [options, setOptions] = React.useState({
    userGroups: [],
  });

  const [error, setError] = React.useState(null);

  async function fetchUserGroups(accountId) {
    const [response, data] = await userGroupsService.getUserGroups({
      accountId,
    });
    if (response.ok) {
      setOptions({ ...options, userGroups: data });
    }
  }

  React.useEffect(() => {
    if (!isOpen) return;

    setOpen(true);
    setLoading(false);
    setError(null);

    if (data?.subscriptions?.length === 0) fetchSubscriptions();
    if (!data?.colorPalettes) fetchColorPalettes();
    //if (!data?.accountManagers) fetchAccountManagers();
    if (!data?.accounts && !data.accountId) fetchAccounts();

    if (action == "create") {
      setValue({
        name: "",
        email: "",
        phone: "",
        logo: "",
        colorPaletteId: 1,
        accountManagerId: 1,
        accountId: data.accountId,
      });
    } else {
      setValue(data?.branchGroup);
    }
  }, [isOpen]);

  React.useEffect(() => {
    if (action != "create") {
      setValue(data?.branchGroup);
    }
  }, [data?.branchGroup]);

  React.useEffect(() => {
    if (!value.accountId) return;

    fetchAccountManagers(value.accountId);
    fetchUserGroups(value.accountId);
  }, [value.accountId]);

  const handleClose = () => {
    setOpen(false);
  };

  const accountManagerOptions = React.useMemo(() => {
    return (
      data?.accountManagers?.map((accountManager) => {
        return {
          id: accountManager.id,
          label: accountManager.name,
        };
      }) || []
    );
  }, [data?.accountManagers]);

  const accountOptions = React.useMemo(() => {
    return (
      data?.accounts?.map((account) => {
        return {
          id: account.id,
          label: account.name,
        };
      }) || []
    );
  }, [data?.accounts]);

  const userGroupOptions = React.useMemo(() => {
    return options.userGroups.map((userGroup) => {
      return {
        id: userGroup.id,
        label: userGroup.name,
      };
    });
  }, [options.userGroups]);

  return (
    <Dialog
      fullWidth
      maxWidth="md"
      open={open}
      onClose={handleClose}
      PaperProps={{
        component: "form",
        onSubmit: async (event) => {
          event.preventDefault();

          setLoading(true);
          delete value.branches;
          delete value.colorPalette;
          if (data.accountId) value.accountId = data.accountId;

          const result = await onSubmit(value);

          if (result.success) {
            handleClose();
          } else {
            console.log("ERROR IS: ", result.error);
            setError(result.error);
            if (!result.error.fields)
              toast.error(`${result.error.message}\n\nTry refreshing the page`);
          }

          setLoading(false);
        },
      }}
    >
      <DialogTitle>
        {action == "edit"
          ? label("edit-branch-group")
          : label("create-branch-group")}
      </DialogTitle>
      <DialogContent>
        <Grid container spacing={2}>
          <Grid container item xs={12} spacing={2}>
            <Grid
              item
              display={"flex"}
              alignItems={"center"}
              justifyContent={"center"}
            >
              <Avatar
                attachment={value.logo == "reset" ? null : value.logo}
                setAttachment={(file) => {
                  setValue({ ...value, logo: file ? file : "reset" });
                }}
                sx={{
                  aspectRatio: "1 !important",
                  height: "calc(100% - 12px)",
                  width: "160px",
                }}
              />
            </Grid>
            <Grid container item xs>
              <Grid item xs={12}>
                <TextField
                  autoFocus
                  required
                  margin="dense"
                  error={error?.fields === "name"}
                  helperText={error?.fields === "name" && error?.message}
                  id="branch group name"
                  name="branch group name"
                  label={label("name-singular")}
                  type="text"
                  value={value.name}
                  onChange={(e) => {
                    setValue({ ...value, name: e.target.value });
                  }}
                  fullWidth
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  autoFocus
                  required
                  margin="dense"
                  error={error?.fields === "email"}
                  helperText={error?.fields === "email" && error?.message}
                  id="branch group email"
                  name="branch group email"
                  label={label("email-singular")}
                  type="text"
                  value={value.email}
                  onChange={(e) => {
                    setValue({ ...value, email: e.target.value });
                  }}
                  fullWidth
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  autoFocus
                  required
                  margin="dense"
                  error={error?.fields === "phone"}
                  helperText={error?.fields === "phone" && error?.message}
                  id="branch group phone"
                  name="branch group phone"
                  label={label("phone-singular")}
                  type="text"
                  value={value.phone}
                  onChange={(e) => {
                    setValue({ ...value, phone: e.target.value });
                  }}
                  fullWidth
                />
              </Grid>
            </Grid>
          </Grid>

          {data.accountId ? null : (
            <Grid item xs={12}>
              <DropdownSelect
                label={label("account-singular")}
                initialValue={value.accountId || ""}
                onChange={(newValue) => {
                  setValue({ ...value, accountId: newValue?.id });
                }}
                items={accountOptions}
                required={true}
                fullWidth
              />
            </Grid>
          )}

          <Grid item xs={6}>
            <MDSelect
              fullWidth
              required
              value={value.colorPaletteId}
              label={label("color-palette-singular")}
              items={(data?.colorPalettes || []).map((cp) => {
                return {
                  value: cp.id,
                  render: (
                    <MDBox
                      display="flex"
                      flexDirection="row"
                      justifyContent="flex-start"
                      alignItems="center"
                      sx={{ width: "100%", gap: ".1rem" }}
                    >
                      <MDBox
                        sx={{
                          flex: 1,
                          whiteSpace: "nowrap",
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                          fontSize: ".8rem",
                          fontWeight: "600",
                        }}
                      >
                        {cp.description}
                      </MDBox>
                      <MDBox
                        display="flex"
                        flexDirection="row"
                        sx={{ flex: 1 }}
                      >
                        <MDBox
                          sx={{
                            height: 24,
                            flex: 3,
                            backgroundColor: cp.color1,
                          }}
                        ></MDBox>
                        <MDBox
                          sx={{
                            flex: 2,
                            height: 24,
                            backgroundColor: cp.color2,
                          }}
                        ></MDBox>
                        <MDBox
                          sx={{
                            flex: 1,
                            height: 24,
                            backgroundColor: cp.color3,
                          }}
                        ></MDBox>
                      </MDBox>
                    </MDBox>
                  ),
                };
              })}
              onChange={(newValue) => {
                setValue({ ...value, colorPaletteId: newValue });
              }}
            />
          </Grid>
          <Grid item xs={6}>
            <DropdownSelect
              disabled={!value.accountId}
              inputProps={
                !value.accountId
                  ? {
                      helperText: label("select-account-first"),
                    }
                  : {}
              }
              label={label("branch-manager-singular")}
              initialValue={
                value.accountManagerId == 0 || !value.accountManagerId
                  ? ""
                  : value.accountManagerId
              }
              onChange={(newValue) => {
                setValue({ ...value, accountManagerId: newValue?.id });
              }}
              items={accountManagerOptions}
              //required={true}
              fullWidth
            />
          </Grid>

          {(
            data?.account ||
            data?.accounts?.find((account) => account.id == value.accountId)
          )?.hasUserGroups ? (
            <>
              <Grid item xs={6}>
                <DropdownSelect
                  label={label("primary-auditor")}
                  items={userGroupOptions}
                  initialValue={value.internalUserGroupId || ""}
                  onChange={(newValue) => {
                    setValue({ ...value, internalUserGroupId: newValue?.id });
                  }}
                  fullWidth
                  required
                />
              </Grid>
              <Grid item xs={6}>
                <DropdownSelect
                  label={label("secondary-auditor")}
                  items={userGroupOptions}
                  initialValue={value.externalUserGroupId || ""}
                  onChange={(newValue) => {
                    setValue({ ...value, externalUserGroupId: newValue?.id });
                  }}
                  fullWidth
                  required
                />
              </Grid>
            </>
          ) : null}

          {/* <Grid item xs={12} sx={{ mt: 1 }}>
            <MSelect
              error={error?.fields === "subscriptionId"}
              helperText={error?.fields === "subscriptionId" && error?.message}
              items={data?.subscriptions.map((item, index) => {
                return {
                  value: item.id,
                  render: item.name,
                };
              })}
              value={value.subscriptionId}
              onChange={(e) => {
                setValue({ ...value, subscriptionId: e });
              }}
              label="Subscription type*"
              required
            />
          </Grid> */}
          <Grid item xs={12}>
            <TextField
              autoFocus
              margin="dense"
              error={error?.fields === "reportExtras"}
              helperText={error?.fields === "reportExtras" && error?.message}
              id="reportExtras"
              name="reportExtras"
              label={label("report-extra-plural")}
              type="text"
              value={value.reportExtras}
              onChange={(e) => {
                setValue({ ...value, reportExtras: e.target.value });
              }}
              fullWidth
              multiline
              minRows={1}
            />
          </Grid>
        </Grid>
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose}>{label("cancel-button")}</Button>
        <LoadingButton
          loading={loading}
          type="submit"
          variant="gradient"
          color="info"
        >
          {action == "edit" ? label("save-button") : label("create-button")}
        </LoadingButton>
      </DialogActions>
    </Dialog>
  );
}
